import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell } from "components/app-table";
import pdfIcon from "assets/images/pages/leave-management/pdf-icon.svg";
import AppStatus from "components/app-status";
import { formatDate } from "common/calendar";

const PageAssignments = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "" });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const [loading, setLoading] = useState(true);
    const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data]);
    const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setLoading(true);
        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            response = await api.get.assignment.list(sanitizeObject(payload));
        } catch (error) {
            const wasCancelled = serveRequestErrors(error);
            if (wasCancelled) return;
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }

        setLoading(false);
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);


    //prettier-ignore
    const AssignmentCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const CertificateCell = useCallback(({ row }) => {
        const status = row.original?.certificateVO?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const SubmissionDateCell = useCallback(({ row }) => {
        const date = row.original?.submissionDate;
        return (<AppTableCell left value={date ? formatDate(date) : "-"} />);
    }, []);

    //prettier-ignore
    const AttachmentCell = useCallback(({ row }) => {
        const file = row.original?.certificateVO;
        const value = file ? (<a href={file?.filePath} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attachment" /></a>) : ("-");
        return (
            <AppTableCell left value={value} />
        );
    }, []);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
        },
        {
            Header: "Assigned By",
            accessor: "assignedByName",
            disableSortBy: false,
        },
        {
            Header: "Submitted On",
            accessor: "submissionDate",
            disableSortBy: false,
            Cell: SubmissionDateCell,
        },
        {
            Header: "Status",
            accessor: "status",
            disableSortBy: false,
            Cell: AssignmentCell,
        },
        {
            Header: "Attachment",
            accessor: "attachment",
            disableSortBy: true,
            Cell: AttachmentCell,
        },
        {
            Header: "Certificate Status",
            accessor: "certificateVO.status",
            disableSortBy: false,
            Cell: CertificateCell,
        },
    ], [AttachmentCell, AssignmentCell, CertificateCell, SubmissionDateCell]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            cancelRequest(COMMON.ENDPOINT_PATH.ASSIGNMENT.LIST);
        };
    }, [cancelRequest]);

    return (
        <div className="page-assignments">
            <div className="assignments">
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton={true} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export default PageAssignments;
